import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SimpleThirdPartyGameHelper } from '../utils/SimpleThirdPartyGameHelper';
import { createArcadePrimitive } from './ArcadePrimitive';
import { createClawMachine } from '../models/ClawMachine';
import { createCoffeeMachinePrimitive } from './CoffeeMachinePrimitive';
import { CharacterControllerBase } from './Character';
import { Inventory } from '../utils/Inventory';
import { InventoryUI } from './InventoryUI';

interface VendingItem {
  name: string;
  price: number;
  imageUrl: string;
  description: string;
  modelCreator: () => THREE.Group;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

// Add debug global function to help with troubleshooting
declare global {
  interface Window {
    // ... existing declarations ...
    debugVendingMachine?: () => void;
  }
}

export class VendingMachineUI {
  private static instance: VendingMachineUI;
  private container: HTMLDivElement;
  private scene: THREE.Scene | null = null;
  private inventory: Inventory;
  private items: VendingItem[] = [
    {
      name: 'Mini Arcade',
      price: 100,
      imageUrl: '/assets/arcade-preview.png',
      description: 'A miniature arcade machine for your collection',
      rarity: 'rare',
      modelCreator: () => {
        const model = createArcadePrimitive('#ff3333', '#4444ff', '#000000');
        model.scale.set(0.2, 0.2, 0.2);
        return model;
      }
    },
    {
      name: 'Toy Claw Machine',
      price: 150,
      imageUrl: '/assets/claw-preview.png',
      description: 'Your very own mini claw machine',
      rarity: 'epic',
      modelCreator: () => {
        const model = createClawMachine();
        model.scale.set(0.2, 0.2, 0.2);
        return model;
      }
    },
    {
      name: 'Coffee Maker',
      price: 200,
      imageUrl: '/assets/coffee-preview.png',
      description: 'A cute coffee machine model',
      rarity: 'legendary',
      modelCreator: () => {
        const model = createCoffeeMachinePrimitive();
        model.scale.set(0.2, 0.2, 0.2);
        return model;
      }
    }
  ];
  
  // Static property to store rendered previews for access by InventoryUI
  public static renderedPreviews: Map<string, string> = new Map();

  private constructor() {
    this.container = document.createElement('div');
    this.container.className = 'vending-machine-ui';
    this.inventory = Inventory.getInstance();
    this.createUI();
    this.createPreviewImages();
  }

  public static getInstance(): VendingMachineUI {
    if (!VendingMachineUI.instance) {
      VendingMachineUI.instance = new VendingMachineUI();
      
      // Add debug function to window for testing
      window.debugVendingMachine = () => {
        console.log('[VendingMachine] Debug function called');
        
        // Check if multiplayerManager exists
        if (!window.multiplayerManager) {
          console.error('[VendingMachine] multiplayerManager not found, cannot initialize debug');
          alert('Error: MultiplayerManager not found. Make sure you are in the game environment.');
          return;
        }
        
        // Give player some points for testing
        window.multiplayerManager.startThirdPartyGameScoring();
        window.multiplayerManager.addPoints(500);
        window.multiplayerManager.stopThirdPartyGameScoring();
        
        // Show the vending machine UI
        VendingMachineUI.instance.show();
        
        console.log('[VendingMachine] Gave player 500 points for testing. Current score:', 
          window.multiplayerManager.getPlayerScore());
      };
    }
    return VendingMachineUI.instance;
  }

  public setScene(scene: THREE.Scene): void {
    this.scene = scene;
  }

  private createUI(): void {
    // Main container
    this.container.innerHTML = `
      <div class="vending-machine-content">
        <div class="vending-machine-header">
          <h2>Vending Machine</h2>
          <div class="score-display">Score: <span id="current-score">0</span></div>
        </div>
        <div class="items-grid">
          ${this.items.map(item => this.createItemCard(item)).join('')}
        </div>
        <button class="close-button">Close</button>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .vending-machine-ui {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
      }

      .vending-machine-content {
        background-color: #2a2a2a;
        border-radius: 10px;
        padding: 20px;
        width: 80%;
        max-width: 800px;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
      }

      .vending-machine-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 2px solid #444;
      }

      .vending-machine-header h2 {
        color: #fff;
        margin: 0;
        font-size: 24px;
      }

      .score-display {
        color: #00ff00;
        font-size: 20px;
        font-weight: bold;
      }

      .items-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 20px;
        margin-bottom: 20px;
      }

      .item-card {
        background-color: #333;
        border-radius: 8px;
        padding: 15px;
        text-align: center;
        transition: transform 0.2s;
      }

      .item-card:hover {
        transform: translateY(-5px);
      }

      .item-image {
        width: 100px;
        height: 100px;
        object-fit: contain;
        margin-bottom: 10px;
      }

      .item-name {
        color: #fff;
        font-size: 18px;
        margin: 10px 0;
      }

      .item-description {
        color: #ccc;
        font-size: 14px;
        margin-bottom: 15px;
      }

      .item-price {
        color: #00ff00;
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 10px;
      }

      .buy-button {
        background-color: #4CAF50;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .buy-button:hover {
        background-color: #45a049;
      }

      .buy-button:disabled {
        background-color: #666;
        cursor: not-allowed;
      }

      .close-button {
        background-color: #ff4444;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        cursor: pointer;
        width: 100%;
        font-size: 16px;
        transition: background-color 0.2s;
      }

      .close-button:hover {
        background-color: #cc0000;
      }

      .message {
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        padding: 10px 20px;
        border-radius: 4px;
        color: white;
        font-weight: bold;
        z-index: 1001;
        animation: fadeInOut 3s ease-in-out;
      }

      @keyframes fadeInOut {
        0% { opacity: 0; transform: translate(-50%, -20px); }
        10% { opacity: 1; transform: translate(-50%, 0); }
        90% { opacity: 1; transform: translate(-50%, 0); }
        100% { opacity: 0; transform: translate(-50%, -20px); }
      }
    `;
    document.head.appendChild(style);

    // Add event listeners
    this.container.querySelector('.close-button')?.addEventListener('click', () => this.hide());
    
    this.updateScoreDisplay();
  }

  private createItemCard(item: VendingItem): string {
    // Ensure image URL is properly formed
    let imageUrl = item.imageUrl;
    if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
      // If it's a relative URL, make sure it's properly formed
      if (!imageUrl.startsWith('/')) {
        imageUrl = '/' + imageUrl;
      }
    }
    
    console.log('[VendingMachine] Creating item card with image URL:', imageUrl);
    
    return `
      <div class="item-card">
        <img src="${imageUrl}" alt="${item.name}" class="item-image" 
             onerror="console.error('Failed to load vending item image:', this.src); this.src='/assets/default-toy.svg'; this.onerror=null;">
        <h3 class="item-name">${item.name}</h3>
        <p class="item-description">${item.description}</p>
        <div class="item-price">${item.price} points</div>
        <button class="buy-button" data-item="${item.name}">Buy</button>
      </div>
    `;
  }

  public show(): void {
    document.body.appendChild(this.container);
    this.updateScoreDisplay();
    this.setupBuyButtons();
    
    // Fix any inventory items with incorrect image paths
    this.fixInventoryItems();
  }

  public hide(): void {
    this.container.remove();
  }

  private setupBuyButtons(): void {
    const buttons = this.container.querySelectorAll('.buy-button');
    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        const itemName = (e.target as HTMLElement).dataset.item;
        const item = this.items.find(i => i.name === itemName);
        if (item) {
          this.handlePurchase(item);
        }
      });
    });
  }

  private handlePurchase(item: VendingItem): void {
    // Get current score from multiplayer manager
    if (!window.multiplayerManager) {
      console.error('MultiplayerManager not initialized');
      
      // Add additional debugging information
      console.error('window object status:', {
        hasMultiplayerManager: !!window.multiplayerManager,
        hasCharacter: !!window.character,
        windowKeys: Object.keys(window).filter(key => !key.startsWith('_')).join(', ')
      });
      
      this.showMessage('Error: Game not initialized properly. Please refresh the page and try again.', 'error');
      return;
    }

    const multiplayerManager = window.multiplayerManager; // Store reference to avoid undefined checks
    const currentScore = multiplayerManager.getPlayerScore();
    console.log('[VendingMachine] Current score:', currentScore, 'Item price:', item.price);
    
    // Debug check of MultiplayerManager
    console.log('[VendingMachine] MultiplayerManager object:', multiplayerManager);
    console.log('[VendingMachine] MultiplayerManager methods:', {
      getPlayerScore: typeof multiplayerManager.getPlayerScore,
      addPoints: typeof multiplayerManager.addPoints,
      startThirdPartyGameScoring: typeof multiplayerManager.startThirdPartyGameScoring,
      stopThirdPartyGameScoring: typeof multiplayerManager.stopThirdPartyGameScoring
    });
    
    // Additional debug info about the item being purchased
    console.log('[VendingMachine] Attempting to purchase item:', {
      name: item.name,
      price: item.price,
      playerHasEnoughPoints: currentScore >= item.price,
      difference: currentScore - item.price
    });
    
    // Check current inventory state before purchase
    const inventoryBefore = this.inventory.getItems();
    console.log('[VendingMachine] Inventory before purchase:', inventoryBefore);
    
    // Check localStorage directly
    const storedInventory = localStorage.getItem('ai_cafe_inventory');
    console.log('[VendingMachine] localStorage inventory before purchase:', storedInventory);
    
    if (currentScore >= item.price) {
      // Add a processing flag to prevent double processing
      let isProcessingPurchase = false;
      
      // Process the purchase directly without waiting for character initialization
      // since we no longer need to spawn objects at the character's position
      if (isProcessingPurchase) {
        console.log('[VendingMachine] Purchase already being processed, ignoring duplicate call');
        return;
      }
      
      // Mark that we're processing the purchase
      isProcessingPurchase = true;
      
      try {
        // Save original score for verification
        const originalScore = multiplayerManager.getPlayerScore();
        console.log('[VendingMachine] Original score before purchase:', originalScore, 'Item price:', item.price);
        
        // Calculate expected new score
        const expectedNewScore = originalScore - item.price;
        console.log('[VendingMachine] Expected new score after purchase:', expectedNewScore);
        
        // Deduct points using multiplayer manager
        console.log('[VendingMachine] Starting point deduction...');
        multiplayerManager.startThirdPartyGameScoring();
        // Ensure we're deducting exactly the item price
        multiplayerManager.addPoints(-item.price);
        multiplayerManager.stopThirdPartyGameScoring();
        
        // Verify score was reduced properly
        const newScore = multiplayerManager.getPlayerScore();
        console.log('[VendingMachine] Actual new score after deduction:', newScore);
        
        // If score deduction failed or deducted wrong amount, show error and exit
        if (newScore >= originalScore) {
          console.error('[VendingMachine] Failed to deduct points. Score not reduced.');
          this.showMessage('Error: Failed to process payment', 'error');
          isProcessingPurchase = false; // Reset processing flag
          return;
        }
        
        // Check if the deduction was for the wrong amount
        if (Math.abs(newScore - expectedNewScore) > 1) { // Allow 1 point tolerance for floating point issues
          console.error(`[VendingMachine] Score deduction amount is incorrect. Expected: ${expectedNewScore}, Got: ${newScore}`);
          
          // Attempt to correct the score
          console.log('[VendingMachine] Attempting to correct score...');
          multiplayerManager.startThirdPartyGameScoring();
          // Add the difference to fix the score
          const correction = expectedNewScore - newScore;
          multiplayerManager.addPoints(correction);
          multiplayerManager.stopThirdPartyGameScoring();
          
          const correctedScore = multiplayerManager.getPlayerScore();
          console.log('[VendingMachine] Score after correction:', correctedScore);
        }

        // Fix image URL to ensure it's absolute
        let imageUrl = item.imageUrl;
        if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
          // If it's a relative URL, make sure it's properly formed
          if (!imageUrl.startsWith('/')) {
            imageUrl = '/' + imageUrl;
          }
        }
        
        // Add the item to the inventory with timestamp
        const inventoryItem = {
          id: `${item.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
          name: item.name,
          description: item.description,
          image: imageUrl,
          rarity: item.rarity
        };
        
        // Add to inventory (this will automatically save to localStorage)
        console.log('[VendingMachine] Adding item to inventory:', inventoryItem);
        this.inventory.addItem(inventoryItem);
        
        // Verify the item was added
        const items = this.inventory.getItems();
        console.log('[VendingMachine] Current inventory after purchase:', items);
        
        // Check localStorage directly after purchase
        const updatedStoredInventory = localStorage.getItem('ai_cafe_inventory');
        console.log('[VendingMachine] localStorage inventory after purchase:', updatedStoredInventory);
        
        // Update the inventory UI if it's currently open
        const inventoryUI = InventoryUI.getInstance();
        inventoryUI.updateItems();
        
        // Show a notification indicator on the inventory button
        inventoryUI.showNotification();
        
        // Fix any inventory items with incorrect image paths
        this.fixInventoryItems();
        
        this.showMessage(`Purchased ${item.name}!`, 'success');
        
        // Update the score display
        this.updateScoreDisplay();
        
        // Reset processing flag
        isProcessingPurchase = false;
      } catch (error) {
        console.error('[VendingMachine] Error during purchase:', error);
        this.showMessage('Error during purchase', 'error');
        // Refund points if purchase fails
        multiplayerManager.startThirdPartyGameScoring();
        multiplayerManager.addPoints(item.price);
        multiplayerManager.stopThirdPartyGameScoring();
        // Reset processing flag
        isProcessingPurchase = false;
      }
    } else {
      this.showMessage('Not enough points!', 'error');
    }
  }

  private updateScoreDisplay(): void {
    const scoreElement = this.container.querySelector('#current-score');
    if (!scoreElement) {
      console.error('[VendingMachine] Score element not found in the DOM');
      return;
    }
    
    try {
      let score = 0;
      
      if (window.multiplayerManager) {
        score = window.multiplayerManager.getPlayerScore();
        console.log('[VendingMachine] Updated score display:', score);
      } else {
        console.warn('[VendingMachine] multiplayerManager not available, showing default score');
        // Default to 0 if no score is available
      }
      
      scoreElement.textContent = score.toString();
    } catch (error) {
      console.error('[VendingMachine] Error updating score display:', error);
      scoreElement.textContent = '0';
    }
  }

  private showMessage(message: string, type: 'success' | 'error'): void {
    const messageElement = document.createElement('div');
    messageElement.className = `message ${type}`;
    
    // For success messages related to purchases, add a "View in Inventory" button
    if (type === 'success' && message.startsWith('Purchased')) {
      messageElement.innerHTML = `
        <div style="margin-bottom: 8px;">${message}</div>
        <button class="view-inventory-btn" style="
          background: #9932cc;
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 12px;
        ">View in Inventory</button>
      `;
      
      // Add event listener to the button
      setTimeout(() => {
        const viewButton = messageElement.querySelector('.view-inventory-btn');
        if (viewButton) {
          viewButton.addEventListener('click', () => {
            // Hide this message
            messageElement.remove();
            
            // Hide vending machine
            this.hide();
            
            // Show inventory
            const inventoryUI = InventoryUI.getInstance();
            inventoryUI.show();
          });
        }
      }, 0);
    } else {
      messageElement.textContent = message;
    }
    
    messageElement.style.backgroundColor = type === 'success' ? '#4CAF50' : '#ff4444';
    document.body.appendChild(messageElement);

    setTimeout(() => {
      messageElement.remove();
    }, 3000);
  }

  // Method to fix inventory items that may have incorrect image paths
  private fixInventoryItems(): void {
    const items = this.inventory.getItems();
    let fixedAny = false;
    
    items.forEach(item => {
      // Check if the image path needs fixing
      if (item.image && !item.image.startsWith('http') && !item.image.startsWith('data:') && !item.image.startsWith('/')) {
        console.log('[VendingMachine] Fixing image path for item:', item.name, 'Current:', item.image);
        
        // Create a fixed item with corrected image path
        const fixedItem = {
          ...item,
          image: '/' + item.image
        };
        
        // Remove old item and add fixed one
        this.inventory.removeItem(item.id);
        this.inventory.addItem(fixedItem);
        fixedAny = true;
      }
    });
    
    if (fixedAny) {
      console.log('[VendingMachine] Fixed one or more inventory items with incorrect image paths');
      // Update inventory UI if it's open
      const inventoryUI = InventoryUI.getInstance();
      inventoryUI.updateItems();
    }
  }

  private createPreviewImages(): void {
    // Create a temporary scene for rendering previews
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x333333);
    
    // Add lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    // Camera setup
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
    camera.position.set(2, 2, 2);
    camera.lookAt(0, 0, 0);

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(200, 200);

    // Render each item's preview
    this.items.forEach(item => {
      const model = item.modelCreator();
      scene.add(model);
      
      // Render and save as image
      renderer.render(scene, camera);
      const imgData = renderer.domElement.toDataURL();
      
      // Store the rendered preview in the static map for InventoryUI to use
      VendingMachineUI.renderedPreviews.set(item.name, imgData);
      
      // Create an image element with the rendered preview
      const img = document.createElement('img');
      img.src = imgData;
      img.className = 'item-image';
      img.alt = item.name;
      
      // Find the corresponding item card and replace the image
      const itemCards = this.container.querySelectorAll('.item-card');
      itemCards.forEach(card => {
        if (card.querySelector('.item-name')?.textContent === item.name) {
          const oldImg = card.querySelector('.item-image');
          if (oldImg) {
            card.replaceChild(img, oldImg);
          }
        }
      });
      
      // Clean up
      scene.remove(model);
    });

    // Clean up
    renderer.dispose();
  }
} 